import React from 'react';
import { Project, ProjectStatus } from '../../types/Project';
import './ProjectList.css';

interface ProjectListProps {
  projects: Project[];
  loading?: boolean;
  onProjectClick: (project: Project) => void;
  onAddProject?: () => void;
  showAddButton?: boolean;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  emptyStateButtonText?: string;
}

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  loading = false,
  onProjectClick,
  onAddProject,
  showAddButton = true,
  emptyStateTitle = "No projects yet",
  emptyStateMessage = "Start by creating your first project to track your progress!",
  emptyStateButtonText = "Create Your First Project"
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.NOT_STARTED: return '#6c757d';
      case ProjectStatus.IN_PROGRESS: return '#007bff';
      case ProjectStatus.COMPLETED: return '#28a745';
      case ProjectStatus.ON_HOLD: return '#ffc107';
      case ProjectStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatStatus = (status: ProjectStatus) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="project-list-loading-overlay">
        <div className="project-list-loading-spinner"></div>
        <p>Loading projects...</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="project-list-empty-state">
        <h3>{emptyStateTitle}</h3>
        <p>{emptyStateMessage}</p>
        {showAddButton && onAddProject && (
          <button className="project-list-add-btn project-list-add-btn-primary" onClick={onAddProject}>
            {emptyStateButtonText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="project-list-grid">
      {projects.map(project => (
        <div 
          key={project.id} 
          className="project-list-card"
          onClick={() => onProjectClick(project)}
          title="Click to view details"
          style={{ cursor: 'pointer' }}
        >
          <div className="project-list-card-header">
            <h3 className="project-list-title">
              {project.name}
            </h3>
            <span 
              className="project-list-status-badge"
              style={{ 
                backgroundColor: getStatusColor(project.status),
                color: '#ffffff'
              }}
            >
              {formatStatus(project.status)}
            </span>
          </div>
          
          {project.description && (
            <p className="project-list-description">{project.description}</p>
          )}
          
          <div className="project-list-progress">
            <div className="project-list-progress-bar">
              <div 
                className="project-list-progress-fill"
                style={{ width: `${project.progress_percentage}%` }}
              ></div>
            </div>
            <span className="project-list-progress-text">{project.progress_percentage}%</span>
          </div>
          
          <div className="project-list-dates">
            {project.start_date && (
              <small>Started: {formatDate(project.start_date)}</small>
            )}
            {project.target_date && (
              <small>Target: {formatDate(project.target_date)}</small>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProjectList;

